import React from 'react';
import {ScrollView} from 'react-native';

import ContentView from '../../components/ui-controls/layout/ContentView';
import Markdown from '../../components/ui-controls/Markdown';
import config from '../../config';
import {AppRoute} from '../../models/AppRoute';
import {
  RootStoreSelectionNavigatorScreenProps,
  RouteNavigatorScreenProps,
} from '../../models/navigationTypes';

// Don't format on save!
// This will live in Airtable:
//  1862-Content configuration in Airtable (T&Cs, etc.)
//  https://dev.azure.com/lmgdevops/branded_website/_workitems/edit/1862

const copy = `
## Gift Card Issuer

Liquor Marketing Group Limited ABN 22 001 440 320
Building 1, Level 5
3 Richardson Place
NORTH RYDE NSW 2113

## Definitions

The following definitions apply to these Terms & Conditions

**Australian Consumer Law** means the law contained in Schedule 2 to the Competition and Consumer Act 2010 (Cth).

**Consumer** has the meaning given in section 3 of the Australian Consumer Law.

**Consumer Guarantees** means the consumer guarantees contained in the Australian Consumer Law and Consumer Guarantee mean any one of them.

**Expiry Date** means the date that is three (3) years after the date of issue/purchase of the Gift Card and after which the Remaining Card Value on any Gift Card will not be available for redemption.

**Gift Card** means any plastic gift card issued by LMG that you have purchased for one or more of the Participating Retailers.

**Gift Card Terms of Use** means the terms and conditions on the Gift Card together with these Terms and Conditions.

**Holder** in respect of a Gift Card means the person who is the owner of the bundle of rights attaching to that Gift Card free from all encumbrances.

**Issuer** means LMG

**LMG** means Liquor Marketing Group Limited ABN 22 001 440 320 of Building 1, Level 5

3 Richardson Place NORTH RYDE NSW 2113.

**Major Failure** has the meaning given to that term in the Australian Consumer Law.

**Participating Retailers** means the retailers specified on the Gift Card at which each of the Gift Cards can be used to purchase goods.

**Remaining Card Value** means, as identified in the System, that portion of the initial value loaded onto the activated Gift Card which is unspent at that time and can be accessed by using the Gift Card in accordance with the Gift Card Terms of Use.

**Service Fee** means a fee charged on the initial value loaded onto the activated Gift Card, as notified by signage at point of sale in the Participating Retailer.

**System** means the database operated by Zen Global Pty Ltd which captures and records gift card data and values.

**Trade Mark** means each trade mark, name or style of business embodying any form of the words “LMG”, “Liquor Marketing Groups”, “${config.APP_NAME}” or any other mark or style owned by LMG or a related entity of LMG.

you or your means a reference to the person who is the purchaser of the Gift Card or is in possession of the Gift Card at the relevant time for purposes of these Terms and Conditions, or who authorises another person to do something with or to the Gift Card on their behalf.

## These Terms and Conditions

1.1 You agree to these Terms and Conditions when you purchase, activate, acquire or attempt to utilise a Gift Card.

1.2 You warrant to the Issuer that you will comply with these Terms and Conditions and all applicable laws.

1.3The Issuer may amend these Terms and Conditions at any time in the following circumstances:

1.3.1 to add or remove a Participating Retailer where a Gift Card may be utilised;

1.3.2 to comply with any applicable law or regulation, or binding judgment, order, standard or determination of any court, tribunal or government or quasi-government agency or body;

1.3.3 for administrative or operational reasons, or to prevent the occurrence of fraud or other unlawful, damaging or unacceptable conduct;

1.3.4 where acting reasonably the Issuer considers the change will not be materially detrimental to you.

1.4 Changes to these Terms and Conditions will be made available at the websites identified in clause 2.4 below.

1.5 These Terms and Conditions are governed by and construed in accordance with the laws and Courts of New South Wales.

1.6 Where you purchase a Gift Card as a personal or business gift, or for other business uses approved by the Issuer, you must ensure that each Holder is made aware of these Terms and Conditions, including the Expiry Date of the Gift Card.

## Purchase of Gift Card

2.1 The Issuer issues each Gift Card.

2.2 A Service Fee may apply to the purchase of a Gift Card.

2.2.1 Applicable Service Fees will be notified by Participating Retailers at point of sale for the Gift Cards in store.

2.2.2 Service Fees are not refundable under any circumstances and do not form part of the Remaining Card Value.

2.3 The Issuer can be contacted in relation to any Gift Card related inquiries at [${config.SUPPORT_EMAIL}](mailto:${config.SUPPORT_EMAIL})

## Using a Gift Card

3.1 A Gift Card has no value unless activated by a Participating Retailer and payment has been made.

3.2 The amount of the Remaining Card Value of an activated Gift Card as identified in the System may only be applied in whole or part payment of the purchase price payable by the Holder to a Participating Retailer.

3.3 A Gift Card cannot be utilised to purchase goods online.

3.4 If the Holder agrees to purchase goods and the Holder tenders an activated Gift Card to a Participating Retailer:

3.4.1 the Issuer will procure the Participating Retailer to accept the amount of the Remaining Card Value towards payment of the price payable by the Holder to the Participating Retailer for the purchase of the goods; and

3.4.2 the Remaining Card Value will be reduced by an amount equivalent to the price payable by the Holder to the Participating Retailer for the purchase of the goods; and

3.4.3 if the price payable by the Holder to the Participating Retailer for the purchase of the goods exceeds the Remaining Card Value, the Holder must pay the difference to the Participating Retailer for the goods using another payment method; or

3.4.4 if there is any Remaining Card Value following the purchase of the goods, no change will be given to the Holder and any Remaining Card Value may only be utilised towards a future purchase of goods from a Participating Retailer until there is no Remaining Card Value, or the occurrence of the Expiry Date, whichever first occurs.

## Expiry Date

4.1 A Gift Card expires on the Expiry Date of that Gift Card. Immediately following the Expiry Date of a Gift Card:

4.1.1 the obligations of the Issuer in respect of that Gift Card are forever discharged, released, extinguished and no longer capable of being enforced; and

4.1.2 the Issuer will have no further obligations in respect of that Gift Card; and

4.1.3 any Remaining Card Value will not be able to be utilised by the Holder or anyone else.

4.2 You need to use all of the Remaining Card Value on a Gift Card before the Expiry Date of that Gift Card.

4.3 A Gift Card can only be activated once and cannot be re-activated at any subsequent time.

## Exclusions

5.1 If a Gift Card is altered or defaced or used as an element of any fraudulent or illegal activity, the amount of the unused value of that Gift Card shall be immediately reduced to nil, and incapable of use by any Holder.

5.2 A person who wishes to use a Gift Card must provide evidence to the reasonable satisfaction of the Issuer that the person is the Holder. If the person is unable to provide evidence to reasonable satisfaction of the Issuer that the person is the Holder, the Issuer will have no obligation to that person in respect of the Gift Card unless and until that person provides that evidence to the reasonable satisfaction of the Issuer.

5.3 A Gift Card cannot be utilised:

5.3.1 by email, phone or facsimile;

5.3.2 to repay outstanding indebtedness of the Holder or any other person, to a Participating Retailer;

5.3.4 to purchase goods from a Participating Retailer that the Participating Retailer is not permitted by law to supply to the Holder.

5.4 A Gift Card cannot be reloaded, used for cash advances or to obtain cash.

5.5 A Gift Card cannot be used to purchase another Gift Card or any other stored value card.

5.6 A Gift Card cannot be exchanged for a different brand, denomination or type of Gift Card or any other stored value card.

5.7 A Gift Card, at all times, remains the property of the Issuer, and must not be resold by a person, without the express prior written consent of the Issuer.

## Transaction Enquiries

6.1 The Remaining Card Value on a Gift Card and Expiry Date of a Gift Card may be checked:

(a) for the Holder at any Participating Retailer; and

(b) by the Holder contacting the Issuer at the email address or phone number set out in clause 2.3.

6.2 The information residing on the System will, in the absence of manifest error, be conclusive evidence of the Remaining Card Value, history and Expiry Date of a Gift Card.

## Lost and Stolen Gift Cards

7.1 A Holder is responsible for the safety of a Gift Card and a lost, stolen, altered or defaced Gift Card will not be reissued, refunded or honoured.

7.2 To the extent permitted by law and subject to clause 8.2, a Gift Card will not be reissued unless:

7.2.1 the Holder proves to the reasonable satisfaction of the Issuer that the Gift Card is faulty or damaged due to the fault of the Issuer or a Participating Retailer; or

7.2.2 the Issuer has acted in breach of any condition or warranty under any applicable laws.

## Liability

8.1 A Holder is responsible for the use and safety of a Gift Card and all transactions involving a Gift Card at all times.

8.2 A Gift Card comes with statutory Consumer Guarantees that cannot be excluded under the Australian Consumer Law. If a Gift Card suffers a Major Failure the Holder is entitled to a replacement or refund of the Gift Card for that Major Failure and claim compensation for any other reasonably foreseeable loss or damage suffered as a result of that Major Failure. The Holder is also entitled to have the Gift Card repaired or replaced if the Gift Card fails to be of acceptable quality and the failure does not amount to a Major Failure.

8.3 To the extent permitted by law, the Issuer, each Participating Retailer and each related entity of the Issuer is not liable to any person for any loss or damage (including direct, indirect or consequential loss) arising out of any use or attempted use of a Gift Card. The Issuer, each Participating Retailer and each related entity of the Issuer will not be in default of these Terms and Conditions for failure to observe or perform any obligations under these Terms and Conditions for any reason or cause which could not, with reasonable diligence, be controlled or prevented. Nothing in these Terms and Conditions is intended to exclude or modify any non-excludable statutory rights under the Australian Consumer Law or any other applicable laws.

## Promotion Activities

9.1 The Holder must not use a Gift Card in connection with any marketing, promotion or advertising activities without the prior express written consent of the Issuer.

9.2 The Trade Marks are owned by LMG or a related entity of LMG.

9.3 Any use of Trade Marks by any third party is strictly prohibited without the prior written consent of the Issuer.

9.4 A Gift Card may not be used in a manner that implies that a person, business, product or service is endorsed or sponsored by or affiliated with the Issuer, a Participating Retailer or any related entity of the Issuer.

## Enquiries and Complaints

10.1 A complaint or enquiry about a Gift Card should be directed to the Issuer at the email address or phone number set out in clause 2.3 above.

11.2 A complaint or enquiry about the quality or nature of goods purchased by a person from a Participating Retailer should be directed to the relevant Participating Retailer from whom that person purchased the goods.

Gift Card Terms of Use Version 1 October 2022
`;

const GiftCardsTermsAndConditionsScreen = (
  _:
    | RouteNavigatorScreenProps<AppRoute.GIFT_CARDS_TERMS_AND_CONDITIONS>
    | RootStoreSelectionNavigatorScreenProps<AppRoute.ROOT_GIFT_CARDS_TERMS_AND_CONDITIONS>,
) => (
  <ContentView>
    <ScrollView>
      <Markdown>{copy}</Markdown>
    </ScrollView>
  </ContentView>
);

export default GiftCardsTermsAndConditionsScreen;
